import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { facturesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, Trash2, CheckCircle, Bell, Clock, FileText, Calendar, AlertTriangle, Edit, Euro, CreditCard, Download, Building2 } from 'lucide-react';

const FacturesPage = () => {
  const { user } = useAuth();
  const profilEntrepriseIncomplet = !user?.raison_sociale?.trim() || !user?.adresse?.trim() || !user?.code_postal?.trim() || !user?.ville?.trim() || !user?.siren?.trim() || !user?.regime_tva;

  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [acompteDialogOpen, setAcompteDialogOpen] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    client_name: '',
    client_siren: '',
    client_adresse: '',
    description: '',
    montant_ht: '',
    tva_rate: '20',
    date_facture: today,
    date_prestation: '',
    date_echeance: ''
  });
  
  const [acompteAmount, setAcompteAmount] = useState('');
  const [acompteNote, setAcompteNote] = useState('');

  const loadFactures = useCallback(async () => {
    try {
      const response = await facturesAPI.getAll();
      setFactures(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFactures();
  }, [loadFactures]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await facturesAPI.create({
        client_name: formData.client_name,
        client_siren: formData.client_siren || null,
        client_adresse: formData.client_adresse || null,
        description: formData.description,
        montant_ht: parseFloat(formData.montant_ht),
        tva_rate: parseFloat(formData.tva_rate),
        date_emission: formData.date_facture,
        date_prestation: formData.date_prestation || null,
        date_echeance: formData.date_echeance || null,
      });
      toast.success('Facture créée');
      setDialogOpen(false);
      resetForm();
      loadFactures();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erreur lors de la création';
      toast.error(msg);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedFacture) return;
    
    try {
      await facturesAPI.update(selectedFacture.id, {
        client_name: formData.client_name,
        client_siren: formData.client_siren || null,
        client_adresse: formData.client_adresse || null,
        description: formData.description,
        montant_ht: parseFloat(formData.montant_ht),
        tva_rate: parseFloat(formData.tva_rate),
        date_emission: formData.date_facture,
        date_prestation: formData.date_prestation || null,
        date_echeance: formData.date_echeance || null,
      });
      toast.success('Facture modifiée');
      setEditDialogOpen(false);
      resetForm();
      loadFactures();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleAddAcompte = async () => {
    if (!selectedFacture || !acompteAmount) return;
    
    try {
      // Utilise le nouvel endpoint avec historique
      await facturesAPI.addPaiement(selectedFacture.id, {
        montant: parseFloat(acompteAmount),
        note: acompteNote || null
      });
      
      const newMontantPaye = (selectedFacture.montant_paye || 0) + parseFloat(acompteAmount);
      
      if (newMontantPaye >= selectedFacture.montant_ttc) {
        toast.success('Facture entièrement payée !');
      } else {
        toast.success(`Acompte de ${formatCurrency(parseFloat(acompteAmount))} enregistré`);
      }
      
      setAcompteDialogOpen(false);
      setAcompteAmount('');
      setAcompteNote('');
      setSelectedFacture(null);
      loadFactures();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement de l\'acompte');
    }
  };

  const handleMarkPaid = async (facture) => {
    try {
      await facturesAPI.update(facture.id, { 
        montant_paye: facture.montant_ttc,
        statut: 'payee', 
        date_paiement: new Date().toISOString() 
      });
      toast.success('Facture marquée comme payée');
      loadFactures();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await facturesAPI.delete(id);
      toast.success('Facture supprimée');
      loadFactures();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (facture) => {
    setSelectedFacture(facture);
    setFormData({
      client_name: facture.client_name,
      client_siren: facture.client_siren || '',
      client_adresse: facture.client_adresse || '',
      description: facture.description,
      montant_ht: facture.montant_ht.toString(),
      tva_rate: facture.tva_rate.toString(),
      date_facture: facture.date_emission ? facture.date_emission.split('T')[0] : today,
      date_prestation: facture.date_prestation ? facture.date_prestation.split('T')[0] : '',
      date_echeance: facture.date_echeance ? facture.date_echeance.split('T')[0] : ''
    });
    setEditDialogOpen(true);
  };

  const openAcompteDialog = (facture) => {
    setSelectedFacture(facture);
    setAcompteAmount('');
    setAcompteNote('');
    setAcompteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ 
      client_name: '', 
      client_siren: '',
      client_adresse: '',
      description: '', 
      montant_ht: '', 
      tva_rate: '20',
      date_facture: new Date().toISOString().split('T')[0],
      date_prestation: '',
      date_echeance: ''
    });
    setSelectedFacture(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const needsReminder = (facture) => {
    if (facture.statut === 'payee') return false;
    const dateFacture = new Date(facture.date_emission);
    const now = new Date();
    const diffDays = Math.floor((now - dateFacture) / (1000 * 60 * 60 * 24));
    return diffDays > 30;
  };

  const getStatusBadge = (facture) => {
    const isOverdue = needsReminder(facture);
    
    if (facture.statut === 'payee') {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Payée
        </Badge>
      );
    }
    
    if (facture.statut === 'acompte' || (facture.montant_paye > 0 && facture.montant_paye < facture.montant_ttc)) {
      return (
        <Badge className="bg-blue-100 text-blue-800 border border-blue-300 flex items-center gap-1">
          <CreditCard className="h-3 w-3" />
          Acompte
        </Badge>
      );
    }
    
    if (isOverdue) {
      return (
        <Badge className="bg-red-100 text-red-800 border border-red-300 flex items-center gap-1 animate-pulse">
          <Bell className="h-3 w-3" />
          À relancer
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
        <Clock className="h-3 w-3" />
        En attente
      </Badge>
    );
  };

  const filteredFactures = factures.filter((f) => {
    const matchSearch = f.client_name.toLowerCase().includes(search.toLowerCase()) ||
                       f.numero.toLowerCase().includes(search.toLowerCase());
    
    if (statusFilter === 'all') return matchSearch;
    if (statusFilter === 'payee') return matchSearch && f.statut === 'payee';
    if (statusFilter === 'acompte') return matchSearch && f.montant_paye > 0 && f.montant_paye < f.montant_ttc;
    if (statusFilter === 'en_attente') return matchSearch && f.statut !== 'payee' && !needsReminder(f) && f.montant_paye === 0;
    if (statusFilter === 'a_relancer') return matchSearch && needsReminder(f);
    
    return matchSearch;
  });

  const stats = {
    total: factures.reduce((sum, f) => sum + f.montant_ttc, 0),
    payees: factures.filter(f => f.statut === 'payee').reduce((sum, f) => sum + f.montant_ttc, 0),
    enAttente: factures.filter(f => f.statut !== 'payee').reduce((sum, f) => sum + (f.reste_a_payer || f.montant_ttc), 0),
    acomptes: factures.filter(f => f.montant_paye > 0 && f.montant_paye < f.montant_ttc).length,
    aRelancer: factures.filter(f => needsReminder(f)).length,
    montantARelancer: factures.filter(f => needsReminder(f)).reduce((sum, f) => sum + f.montant_ttc, 0),
  };

  return (
    <div className="space-y-6" data-testid="factures-page">
      {profilEntrepriseIncomplet && (
        <Card className="border-amber-300 bg-amber-50" data-testid="profil-incomplet-banner">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <Building2 className="h-5 w-5 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-900">Profil entreprise incomplet — création de facture bloquée</p>
              <p className="text-sm text-amber-800">
                Pour émettre des factures 100 % conformes (Art. 289 CGI + Factur-X EN 16931), renseignez raison sociale, adresse complète (rue, code postal, ville), SIREN et régime de TVA.
              </p>
            </div>
            <Link to="/profil-entreprise">
              <Button variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" data-testid="btn-complete-profil">
                Compléter mon profil
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Factures Clients</h1>
          <p className="text-muted-foreground">Suivi de vos factures et acomptes</p>
        </div>
        
        <Dialog open={dialogOpen && !profilEntrepriseIncomplet} onOpenChange={(open) => !profilEntrepriseIncomplet && setDialogOpen(open)}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-facture-btn"
              disabled={profilEntrepriseIncomplet}
              title={profilEntrepriseIncomplet ? "Complétez d'abord votre profil entreprise" : ""}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle facture
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer une facture</DialogTitle>
              <DialogDescription>Renseignez les informations de votre facture. Vos coordonnées seront ajoutées automatiquement.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Date d'émission
                  </Label>
                  <Input
                    type="date"
                    value={formData.date_facture}
                    onChange={(e) => setFormData({ ...formData, date_facture: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de prestation</Label>
                  <Input
                    type="date"
                    value={formData.date_prestation}
                    onChange={(e) => setFormData({ ...formData, date_prestation: e.target.value })}
                    data-testid="input-date-prestation"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date d'échéance <span className="text-xs text-muted-foreground font-normal">(facultatif)</span></Label>
                <Input
                  type="date"
                  value={formData.date_echeance}
                  onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
                  data-testid="input-date-echeance"
                />
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="Nom du client"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Adresse du client <span className="text-xs text-muted-foreground font-normal">(recommandé, mention légale)</span></Label>
                <textarea
                  value={formData.client_adresse}
                  onChange={(e) => setFormData({ ...formData, client_adresse: e.target.value })}
                  placeholder={"12 rue de la République\n75001 Paris"}
                  rows={2}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
                  data-testid="input-client-adresse"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  SIREN client <span className="text-xs text-muted-foreground font-normal">(facultatif — utile pour la réforme 2026)</span>
                </Label>
                <Input
                  value={formData.client_siren}
                  onChange={(e) => setFormData({ ...formData, client_siren: e.target.value.replace(/\s/g, '') })}
                  placeholder="123456789 (si client professionnel)"
                  maxLength={9}
                  pattern="[0-9]{9}"
                  data-testid="input-client-siren"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Prestation réalisée"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant HT (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.montant_ht}
                    onChange={(e) => setFormData({ ...formData, montant_ht: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>TVA (%)</Label>
                  <select
                    value={formData.tva_rate}
                    onChange={(e) => setFormData({ ...formData, tva_rate: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="0">0% (Franchise)</option>
                    <option value="5.5">5.5%</option>
                    <option value="10">10%</option>
                    <option value="20">20%</option>
                  </select>
                </div>
              </div>
              <Button type="submit" className="w-full">
                Créer la facture
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dialog Modifier Facture */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la facture {selectedFacture?.numero}</DialogTitle>
            <DialogDescription>Ajustez les informations de la facture.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date d'émission</Label>
                <Input
                  type="date"
                  value={formData.date_facture}
                  onChange={(e) => setFormData({ ...formData, date_facture: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Date de prestation</Label>
                <Input
                  type="date"
                  value={formData.date_prestation}
                  onChange={(e) => setFormData({ ...formData, date_prestation: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date d'échéance</Label>
              <Input
                type="date"
                value={formData.date_echeance}
                onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Client</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Adresse du client</Label>
              <textarea
                value={formData.client_adresse}
                onChange={(e) => setFormData({ ...formData, client_adresse: e.target.value })}
                placeholder={"12 rue de la République\n75001 Paris"}
                rows={2}
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                SIREN client <span className="text-xs text-muted-foreground font-normal">(facultatif)</span>
              </Label>
              <Input
                value={formData.client_siren}
                onChange={(e) => setFormData({ ...formData, client_siren: e.target.value.replace(/\s/g, '') })}
                placeholder="123456789"
                maxLength={9}
                pattern="[0-9]{9}"
                data-testid="input-client-siren-edit"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.montant_ht}
                  onChange={(e) => setFormData({ ...formData, montant_ht: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>TVA (%)</Label>
                <select
                  value={formData.tva_rate}
                  onChange={(e) => setFormData({ ...formData, tva_rate: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="0">0%</option>
                  <option value="5.5">5.5%</option>
                  <option value="10">10%</option>
                  <option value="20">20%</option>
                </select>
              </div>
            </div>
            <Button type="submit" className="w-full">
              Enregistrer les modifications
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Acompte */}
      <Dialog open={acompteDialogOpen} onOpenChange={setAcompteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enregistrer un acompte</DialogTitle>
          </DialogHeader>
          {selectedFacture && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p className="font-medium">{selectedFacture.client_name}</p>
                <p className="text-sm text-muted-foreground">{selectedFacture.numero}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Montant TTC</p>
                    <p className="font-bold">{formatCurrency(selectedFacture.montant_ttc)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Déjà payé</p>
                    <p className="font-bold text-blue-600">{formatCurrency(selectedFacture.montant_paye || 0)}</p>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">Reste à payer</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(selectedFacture.reste_a_payer || selectedFacture.montant_ttc)}</p>
                </div>
              </div>
              
              {/* Historique des paiements */}
              {selectedFacture.paiements && selectedFacture.paiements.length > 0 && (
                <div className="border rounded-lg p-3">
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Historique des paiements ({selectedFacture.paiements.length})
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedFacture.paiements.map((p, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm bg-green-50 p-2 rounded">
                        <div>
                          <span className="text-gray-600">{formatDate(p.date)}</span>
                          {p.note && <span className="text-gray-500 ml-2">- {p.note}</span>}
                        </div>
                        <span className="font-semibold text-green-700">+{formatCurrency(p.montant)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Montant de l'acompte (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={acompteAmount}
                  onChange={(e) => setAcompteAmount(e.target.value)}
                  placeholder="0.00"
                  max={selectedFacture.reste_a_payer || selectedFacture.montant_ttc}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Note (optionnel)</Label>
                <Input
                  value={acompteNote}
                  onChange={(e) => setAcompteNote(e.target.value)}
                  placeholder="Ex: Virement, Chèque n°123..."
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleAddAcompte} 
                  className="flex-1"
                  disabled={!acompteAmount || parseFloat(acompteAmount) <= 0}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Enregistrer l'acompte
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setAcompteAmount((selectedFacture.reste_a_payer || selectedFacture.montant_ttc).toString());
                  }}
                >
                  Solder
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Alerte relances */}
      {stats.aRelancer > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-full">
                <Bell className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-red-900">
                  {stats.aRelancer} facture{stats.aRelancer > 1 ? 's' : ''} à relancer
                </p>
                <p className="text-sm text-red-700">
                  {formatCurrency(stats.montantARelancer)} en attente depuis plus de 30 jours
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => setStatusFilter('a_relancer')}
              >
                Voir
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Total</p>
            <p className="text-xl font-bold">{formatCurrency(stats.total)}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-1 mb-1">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <p className="text-sm text-emerald-700">Encaissé</p>
            </div>
            <p className="text-xl font-bold text-emerald-800">{formatCurrency(stats.payees)}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-1 mb-1">
              <CreditCard className="h-4 w-4 text-blue-600" />
              <p className="text-sm text-blue-700">Acomptes</p>
            </div>
            <p className="text-xl font-bold text-blue-800">{stats.acomptes}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-1 mb-1">
              <Clock className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-700">En attente</p>
            </div>
            <p className="text-xl font-bold text-amber-800">{formatCurrency(stats.enAttente)}</p>
          </CardContent>
        </Card>
        <Card className={stats.aRelancer > 0 ? "border-red-200 bg-red-50" : ""}>
          <CardContent className="p-4">
            <div className="flex items-center gap-1 mb-1">
              <Bell className={`h-4 w-4 ${stats.aRelancer > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
              <p className={`text-sm ${stats.aRelancer > 0 ? 'text-red-700' : 'text-muted-foreground'}`}>
                À relancer
              </p>
            </div>
            <p className={`text-xl font-bold ${stats.aRelancer > 0 ? 'text-red-800' : ''}`}>
              {stats.aRelancer}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'en_attente', 'acompte', 'a_relancer', 'payee'].map((filter) => (
                <Button
                  key={filter}
                  variant={statusFilter === filter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(filter)}
                >
                  {filter === 'all' && 'Toutes'}
                  {filter === 'en_attente' && 'En attente'}
                  {filter === 'acompte' && 'Acomptes'}
                  {filter === 'a_relancer' && 'À relancer'}
                  {filter === 'payee' && 'Payées'}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N°</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Montant TTC</TableHead>
                <TableHead className="text-right">Payé</TableHead>
                <TableHead className="text-right">Reste</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredFactures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Aucune facture trouvée
                  </TableCell>
                </TableRow>
              ) : (
                filteredFactures.map((facture) => (
                  <TableRow key={facture.id}>
                    <TableCell className="font-medium">{facture.numero}</TableCell>
                    <TableCell>{formatDate(facture.date_emission)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{facture.client_name}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {facture.description}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(facture.montant_ttc)}
                    </TableCell>
                    <TableCell className="text-right">
                      {facture.montant_paye > 0 ? (
                        <span className="text-blue-600 font-medium">
                          {formatCurrency(facture.montant_paye)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {facture.statut !== 'payee' ? (
                        <span className="text-amber-600 font-medium">
                          {formatCurrency(facture.reste_a_payer || facture.montant_ttc)}
                        </span>
                      ) : (
                        <span className="text-emerald-600">0 €</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(facture)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(facture)}
                          title="Modifier"
                          data-testid={`facture-edit-btn-${facture.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {facture.statut !== 'payee' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAcompteDialog(facture)}
                              title="Acompte"
                          data-testid={`facture-acompte-btn-${facture.id}`}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <CreditCard className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkPaid(facture)}
                              title="Marquer payée"
                          data-testid={`facture-mark-paid-btn-${facture.id}`}
                              className="text-emerald-600 hover:text-emerald-700"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => facturesAPI.downloadPDF(facture.id)}
                          className="h-8 w-8 p-0"
                          title="Télécharger PDF"
                          data-testid={`facture-download-pdf-btn-${facture.id}`}
                        >
                          <Download className="h-4 w-4 text-gray-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(facture.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Supprimer"
                          data-testid={`facture-delete-btn-${facture.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default FacturesPage;
